import { useAction, useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { AnimateHeight, Drawer } from '@root/shared/ui-kit'

import { InputTransferStep, type TransferFlowReatom } from '../../../atoms'

import { TransferProgress } from './transfer-progress'
import { TransferReviewContent } from './transfer-review-content'

import type { DrawerProps } from '@root/shared/ui-kit'

type TransferReviewDrawerProps = {
  model: TransferFlowReatom
  onDone?: () => void
} & Pick<DrawerProps, 'onOpenChange' | 'open'>

export const TransferReviewDrawer: React.FC<TransferReviewDrawerProps> = ({
  open,
  onOpenChange,
  model,
  onDone,
}) => {
  const { t } = useTranslation()
  const [state] = useAtom(model.transferStateAtom)
  const [_, setStep] = useAtom(model.stepAtom)
  const resetTransferState = useAction(model.transferStateAtom.reset)

  const renderContent = () => {
    if (AsyncStateN.isReady(state)) {
      return (
        <TransferProgress
          onContinue={() => {
            onDone?.()
          }}
          state={state}
        />
      )
    }

    return (
      <TransferReviewContent
        model={model}
        onReject={() => {
          setStep(new InputTransferStep())
        }}
      />
    )
  }

  return (
    <Root
      open={open}
      onOpenChange={(open) => {
        onOpenChange?.(open)
        if (!open) {
          if (AsyncStateN.isError(state)) {
            resetTransferState()
            return
          }
          if (AsyncStateN.isSuccess(state)) {
            onDone?.()
            return
          }
        }
      }}
      title={t('transfer')}
    >
      <AnimateHeight>
        <Content>{renderContent()}</Content>
      </AnimateHeight>
    </Root>
  )
}

const Root = styled(Drawer)``

const Content = styled.div`
  padding-block: 8px var(--safe-bottom);

  display: grid;
`
